import React, { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ShortenLink } from 'types/LinkShortener';
import UtmTagBuilder from 'components/shared/UtmTagBuilder';
import { getHostname, prependHttp } from 'utils/url';
import {
  hasUrlAnyUtmTag,
  stripUtmTags,
} from 'components/shared/utmTagBuilder/UtmTagBuilder.utils';
import { Module } from 'config';
import {
  Button,
  Field,
  Icon,
  TextInput,
  Toggle,
  Tooltip,
} from '@kontentino/ui';
import { faQuestionCircle } from '@fortawesome/pro-regular-svg-icons/faQuestionCircle';
import Modal from 'components/shared/Modal';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

type Props = {
  onClose: () => void;
  onSave(values: Omit<FormState, 'showCustomUrl' | 'showUtmTags'>): void;
  onSaveAndInsert(
    values: Omit<FormState, 'showCustomUrl' | 'showUtmTags'>,
  ): void;
  defaultValues?: ShortenLink | null;
  isLoading: boolean;
  pageId?: number;
};

type FormState = z.infer<typeof schema>;

const schema = z.object({
  name: z.string().max(Module.Pages.LINK_SHORTENER_NAME_MAX_CHARS),
  url: z.string().url(),
  id: z.string(),
  source: z.enum(['legacy', 'shortio'] as const).optional(),
  pageId: z.string().nullish(),
  note: z.string().nullish(),
  customSlug: z.string().nullish(),
  showCustomUrl: z.boolean(),
  showUtmTags: z.boolean(),
});

const Form: FC<Props> = (props) => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    register,
    watch,
    control,
    formState,
    getValues,
    setValue,
  } = useForm<FormState>({
    resolver: zodResolver(schema),
    defaultValues: parseDefaultValues(),
  });
  const isEditing = !!props.defaultValues?._id;

  function parseDefaultValues(): Partial<FormState> {
    if (props.defaultValues) {
      const domain = prependHttp(getHostname(props.defaultValues.shortUrl));
      const customSlug = props.defaultValues.shortUrl
        .replace(domain, '')
        .slice(1);

      return {
        ...props.defaultValues,
        showCustomUrl: !!props.defaultValues._id,
        customSlug,
        showUtmTags: hasUrlAnyUtmTag(props.defaultValues.url),
      };
    }

    return {
      id: '',
      url: '',
      pageId: props.pageId ? String(props.pageId) : null,
      showCustomUrl: false,
      showUtmTags: false,
    };
  }

  const [showUtmTags, showCustomUrl, url, name, source] = watch([
    'showUtmTags',
    'showCustomUrl',
    'url',
    'name',
    'source',
  ]);
  const isLegacy = source === 'legacy';

  function onSubmit(
    callback: (value: Omit<FormState, 'showCustomUrl' | 'showUtmTags'>) => void,
  ) {
    return handleSubmit(({ showCustomUrl, showUtmTags, ...values }) =>
      callback(values),
    );
  }

  return (
    <form>
      <Modal.Content className="tw-flex tw-flex-col tw-gap-4">
        <input type="hidden" {...register('id')} />
        <Field.Group>
          <Field.Label required>{t('linkShortener.url')}</Field.Label>
          <TextInput
            required
            {...register('url')}
            placeholder="https://"
            onBlur={() => {
              if (getValues().url !== '') {
                setValue('url', prependHttp(getValues().url));
              }
            }}
            error={!!formState.errors.url?.message}
          />
          <Field.Error>{formState.errors.url?.message}</Field.Error>
        </Field.Group>
        <Field.Group>
          <Field.Label>{t('linkShortener.name')}</Field.Label>
          <TextInput
            {...register('name')}
            placeholder={t('linkShortener.namePlaceholder')}
            error={!!formState.errors.name?.message}
          />
          <Field.Error>{formState.errors.name?.message}</Field.Error>
        </Field.Group>
        {isLegacy && (
          <Field.Group>
            <Field.Label>{t('linkShortener.note')}</Field.Label>
            <TextInput {...register('note')} />
          </Field.Group>
        )}
        <Field.Group>
          <Controller
            defaultValue={false}
            name="showCustomUrl"
            control={control}
            render={({ field: { value, onChange } }) => (
              <>
                <div className="tw-flex tw-gap-2">
                  <Field.Label>{t('linkShortener.customUrl')}</Field.Label>
                  <Toggle
                    checked={value}
                    onChange={() => onChange(!value)}
                    disabled={isEditing}
                  />
                </div>
                {showCustomUrl && (
                  <TextInput
                    {...register('customSlug')}
                    placeholder={t('linkShortener.customUrlPlaceholder')}
                    disabled={isEditing}
                  />
                )}
              </>
            )}
          />
        </Field.Group>
        <Field.Group>
          <Controller
            name="pageId"
            control={control}
            render={({ field: { value, onChange } }) => (
              <>
                <div className="tw-flex tw-items-center tw-gap-2">
                  <Field.Label required>
                    {t('linkShortener.globalLink')}
                  </Field.Label>
                  <Toggle
                    checked={value === null}
                    onChange={() =>
                      onChange(
                        typeof value === 'string' ? null : String(props.pageId),
                      )
                    }
                  />
                  <Tooltip content={t('linkShortener.globalLinkTooltip')}>
                    <Icon icon={faQuestionCircle} className="tw-ml-2" />
                  </Tooltip>
                </div>
              </>
            )}
          />
        </Field.Group>
        <Controller
          defaultValue={false}
          name="showUtmTags"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Field.Group>
              <div className="tw-flex tw-items-center tw-gap-2">
                <Field.Label required>{t('linkShortener.utmTags')}</Field.Label>
                <Toggle
                  checked={value}
                  onChange={() => {
                    const newValue = !value;
                    onChange(newValue);

                    if (!newValue) {
                      setValue('url', stripUtmTags(getValues('url')));
                    }
                  }}
                />
              </div>
            </Field.Group>
          )}
        />
        {showUtmTags && (
          <Field.Group>
            <UtmTagBuilder
              value={url}
              onChange={(url) => setValue('url', url)}
              parseFieldsOnUrlsChange
            />
          </Field.Group>
        )}
      </Modal.Content>
      <Modal.Footer>
        <Button variant="secondary" type="button" onClick={props.onClose}>
          {t('close')}
        </Button>
        <Button
          variant="secondary"
          isLoading={props.isLoading}
          type="button"
          onClick={onSubmit(props.onSaveAndInsert)}
        >
          {name
            ? t('linkShortener.saveAndInsert')
            : t('linkShortener.shortenAndInsert')}
        </Button>
        <Button
          variant="primary"
          style={{ marginLeft: 8 }}
          isLoading={props.isLoading}
          type="button"
          onClick={onSubmit(props.onSave)}
        >
          {t('linkShortener.save')}
        </Button>
      </Modal.Footer>
    </form>
  );
};

export default Form;
